import { createPortal } from 'react-dom';
import LiquidText, { liquidTone } from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import './Trabalhadores.css';

// Sempre o mesmo objeto: limpar o histórico ao fechar o modal não obriga a
// desenhar outra vez o que já estava vazio.
const HISTORICO_VAZIO = { evidencias: [], aceitacoes: [] };
const dataPt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');
const dataHojeLisboa = () => {
    const partes = new Intl.DateTimeFormat('pt-PT', {
        timeZone: 'Europe/Lisbon',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date());
    const valor = tipo => partes.find(parte => parte.type === tipo)?.value;
    return [valor('year'), valor('month'), valor('day')].join('-');
};

const Trabalhadores = ({ integrado = false, acoesDestino }) => {
    const liquidFeedback = useLiquidFeedback();
    const [trabalhadores, setTrabalhadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('pessoais');
    const workerContentRef = useRef(null);
    useEffect(() => {
        if (workerContentRef.current) workerContentRef.current.scrollTop = 0;
    }, [activeTab, showModal]);
    const [selectedTrabalhador, setSelectedTrabalhador] = useState(null);
    /* Evidências de conformidade legal deste trabalhador — o mesmo documento
       que está em Req. Legais > Evidências, visto do lado da pessoa. Numa
       auditoria a amostragem é por colaborador, e a ficha de aptidão tem de
       estar onde a auditora a vai pedir. */
    const [evidencias, setEvidencias] = useState([]);
    /* Formação frequentada por este trabalhador, com certificado e eficácia. */
    const [formacaoTrab, setFormacaoTrab] = useState([]);
    /* Documentos desta pessoa que já não estão em vigor: fichas substituídas
       por outra mais recente e aceitações de versões anteriores de documentos
       internos. Saíram das outras abas e ficam na aba Histórico — guardam-se,
       porque a auditoria pede o histórico. */
    const [historico, setHistorico] = useState(HISTORICO_VAZIO);
    const nHistorico = historico.evidencias.length + historico.aceitacoes.length;
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    /* Abre em "Ativos" de propósito: quem entra aqui quer o pessoal da casa, e
       os inativos só interessam quando se vai procurar alguém que já saiu. */
    const [filterStatus, setFilterStatus] = useState('ativos');
    const [epiHojeAProcessar, setEpiHojeAProcessar] = useState(null);

    // Signature Pad State (native canvas)
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    /* O mesmo quadro de desenho serve a assinatura e a rubrica: guarda-se aqui
       para qual dos dois campos o traço vai. Sem isto, a rubrica escreveria por
       cima da assinatura. */
    const [alvoAssinatura, setAlvoAssinatura] = useState('assinatura_url');
    /* Data lida do PDF do contrato, proposta enquanto o campo estiver vazio. */
    const [dataSugerida, setDataSugerida] = useState(null);
    const sigCanvasRef = useRef(null);
    const isDrawingRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });

    // Camera State
    const [showCameraModal, setShowCameraModal] = useState(false);
    const videoRef = useRef(null);
    const [cameraError, setCameraError] = useState('');

    // Documents State
    const [workerDocs, setWorkerDocs] = useState([]);
    /* Um cartão só passa a "aceite" quando o documento base existir na
       aplicação — aceitar sem documento não é evidência. Os que ainda não têm
       minuta ficam Pendentes de propósito, até a Gerência localizar as minutas
       oficiais no servidor (decisão de 18-08-2026). */
    const AVAILABLE_DOCS = [
        'Código de Conduta',
        'Regulamento Interno',
        'Política de Privacidade',
        'Consentimento de Proteção de Dados (RGPD)',
        'Manual de Acolhimento e Segurança',
        'Ficha de Entrega de EPIs',
        'Declaração de Isenção de Horário (se aplicável)',
        'Acordo de Confidencialidade',
        'Manual de Gestão',
        'Comunicação de Perigos e Riscos aos Trabalhadores',
        'Regras Procedimentos em Postos de Combustível',
        'Ficha de Procedimentos de Segurança'
    ];

    // Internal Docs State
    const [documentosInternos, setDocumentosInternos] = useState([]);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [currentDocName, setCurrentDocName] = useState(null);
    const [showManageDocs, setShowManageDocs] = useState(false);

    // Cartões de débito entregues a esta pessoa. São vários (o físico e o
    // online) e é esta ligação que diz a quem pedir as faturas em falta.
    const [todosCartoes, setTodosCartoes] = useState([]);
    const [cartoesSel, setCartoesSel] = useState([]);
    // Cartões desta pessoa com o visto «Manutenção» (Obra ou Manutenção ao registar despesas)
    const [manutencaoSel, setManutencaoSel] = useState([]);
    /* A ficha que está aberta agora. Uma resposta que chegue depois de se abrir
       outra ficha é ignorada: sem isto, a lista de cartões de uma pessoa podia
       entrar na ficha seguinte e ser gravada nela quando o servidor demorava a
       responder (provável causa de, a 17-09-2026, os cartões do Francisco terem
       ido parar à ficha de outra pessoa durante um reinício do servidor). */
    const fichaAbertaRef = useRef(null);
    // A lista de cartões desta pessoa chegou? Sem ela não se gravam cartões.
    const [cartoesCarregados, setCartoesCarregados] = useState(false);
    const [projetoManutencao, setProjetoManutencao] = useState(null);

    useEffect(() => {
        fetchDocumentosInternos();
    }, []);

    const fetchDocumentosInternos = async () => {
        try {
            const res = await api.get('/trabalhadores/documentos-internos');
            setDocumentosInternos(res.data);
        } catch (err) {
            console.error('Erro ao buscar documentos internos:', err);
        }
    };

    const initialFormState = {
        user_id: null,
        foto_url: null,
        numero_trabalhador: '',
        n_trabalhador_interno: '',
        nome: '',
        residencia: '',
        categoria_profissional: '',
        empresa: 'SPROD, LDA',
        doc_identificacao_tipo: 'CC',
        doc_identificacao_num: '',
        doc_identificacao_validade: '',
        data_nascimento: '',
        nacionalidade: 'Portuguesa',
        nif: '',
        niss: '',
        estrangeiro_num_visto: '',
        estrangeiro_validade_visto: '',
        estrangeiro_contrato_act: false,
        aptidao_data_exame: '',
        aptidao_validade: '',
        aptidao_resultado: '',
        habilitacao_equipamentos: 'Não',
        habilitado_conduzir: false,
        epi_entregue: false,
        epi_data_entrega: '',
        formacao_profissional_data: '',
        formacao_acolhimento_data: '',
        formacao_stc: false,
        formacoes_especificas: '',
        observacoes: '',
        assinatura_url: '',
        rubrica_url: '',
        contrato_data: '',
        contrato_ja_assinado: false,
        contrato_alerta_ativo: false,
        contrato_termo_maximo: '',
        contrato_duracao_dias: '',
        contrato_preaviso_dias: 15,
        ativo: true,
        telefone: '',
        email: '',
        contacto_emergencia: '',
        iban: '',
        localidade: '',
        codigo_postal: '',
        telefone_fixo: '',
        telefone_alt: '',
        naturalidade_distrito: '',
        naturalidade_concelho: '',
        naturalidade_freguesia: '',
        habilitacoes_literarias: '',
        doc_identificacao_emissao: '',
        contrato_data_inicio: '',
        contrato_data_fim: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [whatsappCopied, setWhatsappCopied] = useState(false);
    useEffect(() => {
        fetchTrabalhadores();
    }, []);

    /* Recarrega sempre que se abre uma ficha — outra, ou a mesma outra vez.
       Fechar o modal não limpa o trabalhador selecionado, e abrir a mesma
       pessoa de novo não o muda: só com o showModal nas dependências se volta
       a pedir, senão ficava à vista o que havia da primeira vez (uma ficha
       carregada entretanto em Req. Legais não aparecia, nem a antiga passava
       para o Histórico). Com o modal fechado as listas esvaziam-se, para a
       ficha seguinte não mostrar por instantes as evidências da anterior. */
    useEffect(() => {
        const id = selectedTrabalhador?.id;
        if (!id || !showModal) { setEvidencias([]); setFormacaoTrab([]); setHistorico(HISTORICO_VAZIO); return; }
        let vivo = true;
        api.get(`/trabalhadores/${id}/evidencias`)
            .then(r => { if (vivo) setEvidencias(r.data || []); })
            .catch(() => { if (vivo) setEvidencias([]); });
        api.get(`/trabalhadores/${id}/formacao`)
            .then(r => { if (vivo) setFormacaoTrab(r.data || []); })
            .catch(() => { if (vivo) setFormacaoTrab([]); });
        api.get(`/trabalhadores/${id}/historico`)
            .then(r => { if (vivo) setHistorico({ evidencias: r.data?.evidencias || [], aceitacoes: r.data?.aceitacoes || [] }); })
            .catch(() => { if (vivo) setHistorico(HISTORICO_VAZIO); });
        return () => { vivo = false; };
    }, [selectedTrabalhador, showModal]);

    /* O endpoint do ficheiro exige autenticação: um window.open direto abre uma
       janela sem token e devolve "Token não fornecido". Busca-se como blob. */
    const abrirEvidencia = async (ev) => {
        try {
            const r = await api.get(`/conformidade-evidencias/${ev.id}/ficheiro`, { responseType: 'blob' });
            const tipo = r.data.type || 'application/octet-stream';
            const url = window.URL.createObjectURL(new Blob([r.data], { type: tipo }));
            if (tipo.includes('pdf')) {
                window.open(url, '_blank');
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = ev.nome_original || 'documento';
                a.click();
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch { liquidFeedback.notify('Não foi possível abrir o documento.'); }
    };

    const fetchTrabalhadores = async () => {
        try {
            const response = await api.get('/trabalhadores');
            setTrabalhadores(response.data || []);
            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar trabalhadores:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const uploadPhotoFile = async (file) => {
        // If we are editing an existing worker, we can upload immediately
        if (selectedTrabalhador && selectedTrabalhador.id) {
            setUploadingPhoto(true);
            const data = new FormData();
            data.append('foto', file);

            try {
                const res = await api.post(`/trabalhadores/${selectedTrabalhador.id}/foto`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setFormData(prev => ({ ...prev, foto_url: res.data.foto_url }));
                // Update local list too
                setTrabalhadores(prev => prev.map(t => t.id === selectedTrabalhador.id ? { ...t, foto_url: res.data.foto_url } : t));
            } catch (err) {
                console.error('Erro ao fazer upload da foto:', err);
                liquidFeedback.notify('Erro ao carregar foto.');
            } finally {
                setUploadingPhoto(false);
            }
        } else {
            liquidFeedback.notify('Por favor, grave a ficha do trabalhador primeiro antes de adicionar a foto.');
        }
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        await uploadPhotoFile(file);
    };

    const startCamera = async () => {
        if (!selectedTrabalhador || !selectedTrabalhador.id) {
            liquidFeedback.notify('Por favor, grave a ficha do trabalhador primeiro antes de adicionar a foto.');
            return;
        }
        setShowCameraModal(true);
        setCameraError('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Erro ao aceder à câmara:", err);
            setCameraError("Não foi possível aceder à câmara. Verifique as permissões do browser.");
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(track => track.stop());
        }
        setShowCameraModal(false);
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;

        const video = videoRef.current;
        if (video.readyState !== 4) return; // not ready

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], "camera-photo.jpg", { type: "image/jpeg" });
            await uploadPhotoFile(file);
            stopCamera();
        }, 'image/jpeg', 0.8);
    };

    /* A rubrica nasce igual à assinatura: quem não tem rubrica própria assina
       na mesma. Só se substitui a cópia enquanto ela for espelho — se já foi
       desenhada uma rubrica à parte, mudar a assinatura não a apaga. */
    const definirAssinatura = (campo, dataUrl) => {
        setFormData(prev => {
            const seguinte = { ...prev, [campo]: dataUrl };
            if (campo === 'assinatura_url'
                && (!prev.rubrica_url || prev.rubrica_url === prev.assinatura_url)) {
                seguinte.rubrica_url = dataUrl;
            }
            return seguinte;
        });
    };

    const handleFileChange = (e, campo = 'assinatura_url') => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => definirAssinatura(campo, reader.result);
            reader.readAsDataURL(file);
        }
        // permite voltar a escolher o mesmo ficheiro depois de o apagar
        e.target.value = '';
    };

    const abrirAssinatura = (campo) => {
        setAlvoAssinatura(campo);
        setShowSignatureModal(true);
        setTimeout(() => clearSignature(), 100);
    };

    const handleSaveSignature = () => {
        const canvas = sigCanvasRef.current;
        if (canvas) {
            definirAssinatura(alvoAssinatura, canvas.toDataURL('image/png'));
            setShowSignatureModal(false);
        }
    };

    const clearSignature = () => {
        const canvas = sigCanvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        if (e.touches) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const startDraw = (e) => {
        const canvas = sigCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        isDrawingRef.current = true;
        const pos = getPos(e, canvas);
        lastPosRef.current = pos;
    };

    const draw = (e) => {
        if (!isDrawingRef.current) return;
        const canvas = sigCanvasRef.current;
        if (!canvas) return;
        e.preventDefault();
        const ctx = canvas.getContext('2d');
        const pos = getPos(e, canvas);
        ctx.beginPath();
        ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        lastPosRef.current = pos;
    };

    const stopDraw = () => {
        isDrawingRef.current = false;
    };

    const handleOpenModal = (trabalhador = null) => {
        fichaAbertaRef.current = trabalhador?.id ?? null;
        if (trabalhador) {
            setSelectedTrabalhador(trabalhador);
            const formatted = { ...trabalhador };
            // Format formatable dates
            const dateFields = ['doc_identificacao_validade', 'doc_identificacao_emissao', 'data_nascimento', 'estrangeiro_validade_visto', 'aptidao_data_exame', 'aptidao_validade', 'epi_data_entrega', 'formacao_profissional_data', 'formacao_acolhimento_data', 'contrato_data_inicio', 'contrato_data_fim'];
            dateFields.forEach(field => {
                if (formatted[field]) formatted[field] = new Date(formatted[field]).toISOString().split('T')[0];
            });
            // Coerce any null DB values to '' so React inputs never get null as value
            const safeFormatted = Object.fromEntries(
                Object.entries(formatted).map(([k, v]) => [k, v === null ? '' : v])
            );
            // Fichas anteriores à rubrica não a têm gravada: mostra-se a
            // assinatura, que é o que passa a valer enquanto não houver rubrica
            // própria. Fica gravada assim que a ficha for guardada.
            if (!safeFormatted.rubrica_url && safeFormatted.assinatura_url) {
                safeFormatted.rubrica_url = safeFormatted.assinatura_url;
            }
            setFormData(safeFormatted);
            // Sem data declarada, lê-se do PDF e preenche-se já o campo — o
            // utilizador confirma ao gravar. Se não der para ler, fica vazio e
            // tem de ser introduzido à mão. A marca "lida do PDF" fica visível
            // enquanto o valor for o que veio do documento.
            setDataSugerida(null);
            if (trabalhador.contrato_pdf_path && !safeFormatted.contrato_data) {
                api.get(`/trabalhadores/${trabalhador.id}/contrato/data-detetada`)
                    .then(r => {
                        if (fichaAbertaRef.current !== trabalhador.id) return;
                        const lida = r.data?.detetada || null;
                        if (!lida) return;
                        setDataSugerida(lida);
                        setFormData(prev => (prev.contrato_data ? prev : { ...prev, contrato_data: lida }));
                    })
                    .catch(() => {});
            }
        } else {
            setSelectedTrabalhador(null);
            setFormData(initialFormState);
        }

        setShowModal(true);
        if (trabalhador) {
            fetchWorkerDocs(trabalhador.id);
        } else {
            setWorkerDocs([]);
        }
        // A lista completa vem sempre: é preciso saber, além dos desta pessoa,
        // quais estão entregues a outra — senão o mesmo cartão era escolhido
        // duas vezes sem ninguém dar por isso.
        api.get('/despesas/cartoes')
            .then(r => setTodosCartoes(r.data || []))
            .catch(() => setTodosCartoes([]));
        setCartoesSel([]);
        setManutencaoSel([]);
        setCartoesCarregados(!trabalhador);
        if (trabalhador) {
            api.get(`/trabalhadores/${trabalhador.id}/cartoes`)
                .then(r => {
                    if (fichaAbertaRef.current !== trabalhador.id) return;
                    setCartoesSel((r.data || []).map(c => c.id));
                    setManutencaoSel((r.data || []).filter(c => c.manutencao).map(c => c.id));
                    setCartoesCarregados(true);
                })
                .catch(() => {});
        }
        // O número vem dos Dados da Empresa: só para o mostrar junto do visto
        api.get('/empresa')
            .then(r => setProjetoManutencao(r.data?.projeto_manutencao || null))
            .catch(() => setProjetoManutencao(null));
    };

    const alternarCartao = (id) => {
        setCartoesSel(prev => prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id]);
        // um cartão que deixa de ser desta pessoa perde o visto
        setManutencaoSel(prev => prev.filter(x => x !== id));
    };

    const alternarManutencao = (id) => {
        setManutencaoSel(prev => prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id]);
    };

    const fetchWorkerDocs = async (id) => {
        try {
            const res = await api.get(`/trabalhadores/${id}/documentos`);
            if (fichaAbertaRef.current !== id) return;
            setWorkerDocs(res.data || []);
        } catch (err) {
            console.error('Error fetching docs:', err);
        }
    };

    const handleOpenDoc = async (docName, currentStatus) => {
        if (!selectedTrabalhador) return;

        // Se já está aceite, permite revogar
        if (currentStatus) {
            if (await liquidFeedback.confirm(`Deseja revogar a aceitação de "${docName}"?`)) {
                submitDocStatus(docName, false, null);
            }
            return;
        }

        // Se não está aceite, tenta abrir o PDF
        const docInternal = documentosInternos.find(d => d.nome === docName);
        if (docInternal && docInternal.pdf_path) {
            setCurrentDocName(docName);

            // Em vez de usar url direta que pode falhar no proxy/router por causa dos headers HTML do iframe,
            // vamos fazer fecth como blob usando o nosso cliente API configurado.
            api.get(`/trabalhadores/documentos-internos/${docInternal.id}/pdf`, { responseType: 'blob' })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    setPdfUrl(url);
                    setShowPdfModal(true);
                })
                .catch(err => {
                    console.error('Erro ao carregar PDF:', err);
                    liquidFeedback.notify('Erro ao carregar o ficheiro PDF.');
                });
        } else {
            // Se não há PDF, permite aceitar manualmente com aviso (ou bloqueia, dependendo da regra rigorosa)
            // Aqui vamos permitir, mas com confirmação de que foi lido em papel físico por ex.
            if (await liquidFeedback.confirm(`O documento "${docName}" não tem PDF digital carregado.\n\nConfirma que o trabalhador leu a versão física e deseja registar a aceitação?`)) {
                handleAcceptDoc(docName); // Aceita sem abrir modal
            }
        }
    };

    const handleAcceptDoc = async (docNameOverride = null) => {
        const docName = docNameOverride || currentDocName;
        if (!docName) return;

        // Verificar assinatura
        if (!formData.assinatura_url) {
            liquidFeedback.notify('⚠️ O trabalhador não tem assinatura registada!\n\nPor favor, vá à aba "Dados Pessoais" e recolha a assinatura antes de aceitar documentos.');
            setShowPdfModal(false);
            return;
        }

        await submitDocStatus(docName, true, formData.assinatura_url);
        setShowPdfModal(false);
    };

    const submitDocStatus = async (docName, newStatus, assinaturaUrl) => {
        try {
            // Optimistic update
            setWorkerDocs(prev => {
                const exists = prev.find(d => d.documento === docName);
                if (exists) {
                    return prev.map(d => d.documento === docName ? { ...d, aceite: newStatus, data_aceitacao: newStatus ? new Date().toISOString() : null } : d);
                } else {
                    return [...prev, { documento: docName, aceite: newStatus, data_aceitacao: new Date().toISOString(), trabalhador_id: selectedTrabalhador.id }];
                }
            });

            await api.post(`/trabalhadores/${selectedTrabalhador.id}/documentos`, {
                documento: docName,
                aceite: newStatus,
                assinatura_url: assinaturaUrl
            });
            // Aceitar ou revogar a versão em vigor muda o que o Histórico diz ao
            // lado das versões anteriores ("aceite a…" / "ainda por aceitar").
            api.get(`/trabalhadores/${selectedTrabalhador.id}/historico`)
                .then(r => setHistorico({ evidencias: r.data?.evidencias || [], aceitacoes: r.data?.aceitacoes || [] }))
                .catch(() => {});
        } catch (err) {
            console.error('Error updating doc:', err);
            liquidFeedback.notify('Erro ao atualizar documento');
            fetchWorkerDocs(selectedTrabalhador.id); // Revert on error
        }
    };

    const handleUploadDoc = async (docName, file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('nome', docName);

        try {
            await api.post('/trabalhadores/documentos-internos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            fetchDocumentosInternos(); // Refresh list
            liquidFeedback.notify(`Documento "${docName}" carregado com sucesso!`);
        } catch (err) {
            console.error(err);
            liquidFeedback.notify('Erro ao carregar documento.');
        }
    };

    const handleDownloadSignedPdf = async (docName, useToday = false) => {
        if (!selectedTrabalhador) return;
        const docInternal = documentosInternos.find(d => d.nome === docName);
        if (!docInternal) {
            liquidFeedback.notify('Este documento não tem PDF carregado.');
            return;
        }
        try {
            const response = await api.get(
                `/trabalhadores/documentos-internos/${docInternal.id}/signed-pdf/${selectedTrabalhador.id}${useToday ? '?date=today' : ''}`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const safeDocName = docName.replace(/[^a-zA-Z0-9]/g, '_');
            const safeName = (selectedTrabalhador.nome || 'trabalhador').replace(/[^a-zA-Z0-9]/g, '_');
            link.setAttribute('download', `${safeDocName}_${safeName}_assinado.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Erro ao descarregar PDF assinado:', err);
            liquidFeedback.notify('Erro ao descarregar o PDF assinado.');
        }
    };

    /* A data do contrato é obrigatória para quem entra a partir de agora. Os que
       já cá estavam ficam de fora — são 22 com contrato e sem data, e exigi-la
       impediria gravar qualquer ficha antiga sem ir buscar o papel primeiro. */
    const OBRIGATORIA_DESDE = '2026-08-11';
    const dataObrigatoria = !!formData.contrato_pdf_path && (
        !selectedTrabalhador ||
        String(selectedTrabalhador.criado_em || '').substring(0, 10) >= OBRIGATORIA_DESDE
    );

    /* Quem é de outra empresa (o técnico de SST, que vem da 4#ALL) não tem
       ficha de aptidão da SPROD: o exame é da entidade que o emprega. Sem isto
       a ficha dele mostrava o aviso amarelo de ficha em falta, a pedir um
       documento que não nos cabe ter. Empresa vazia conta como da casa. */
    const empresaTrab = String(formData.empresa || '').trim();
    const externo = !!empresaTrab && !/sprod/i.test(empresaTrab);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (dataObrigatoria && !formData.contrato_data) {
            liquidFeedback.notify('Indique a data do contrato.\n\nNão foi possível lê-la do PDF, '
                + 'e é ela que vai para o contrato assinado e para o controlo de renovações.');
            return;
        }
        try {
            let idTrabalhador = selectedTrabalhador?.id;
            if (selectedTrabalhador) {
                await api.put(`/trabalhadores/${selectedTrabalhador.id}`, formData);
            } else {
                const criado = await api.post('/trabalhadores', formData);
                idTrabalhador = criado.data?.id;
            }
            // Os cartões estão noutra tabela e gravam-se a seguir: num
            // trabalhador novo o id só existe depois do POST. Se esta parte
            // falhar, diz-se — calar levava a ficha a mostrar cartões que não
            // ficaram guardados.
            // Sem a lista desta pessoa carregada, a escolha está vazia e gravá-la
            // tirava-lhe todos os cartões: nesse caso os cartões não se gravam.
            if (idTrabalhador && !cartoesCarregados) {
                if (cartoesSel.length) {
                    liquidFeedback.notify('O trabalhador foi gravado, mas os cartões associados não: a lista de cartões '
                        + 'desta pessoa não chegou a carregar.\n\nVolte a abrir a ficha e tente outra vez.');
                }
            } else if (idTrabalhador) {
                try {
                    await api.put(`/trabalhadores/${idTrabalhador}/cartoes`, {
                        cartao_ids: cartoesSel,
                        manutencao_ids: manutencaoSel.filter(id => cartoesSel.includes(id)),
                    });
                } catch (err) {
                    console.error('Erro ao guardar cartões associados:', err);
                    liquidFeedback.notify('O trabalhador foi gravado, mas os cartões associados não.\n\n'
                        + 'Volte a abrir a ficha e tente outra vez no separador Cartões Associados.');
                }
            }
            fetchTrabalhadores();
            setShowModal(false);
            setFormData(initialFormState);
        } catch (error) {
            console.error('Erro ao salvar:', error);
            liquidFeedback.notify('Erro ao salvar trabalhador');
        }
    };

    const handleGenerateUser = async () => {
        if (!selectedTrabalhador) return;
        if (formData.user_id) {
            liquidFeedback.notify('Este trabalhador já tem um utilizador associado.');
            return;
        }

        if (!await liquidFeedback.confirm(`Deseja gerar automaticamente um utilizador mobile para ${formData.nome}?`)) {
            return;
        }

        try {
            const res = await api.post(`/trabalhadores/generate-user/${selectedTrabalhador.id}`);
            liquidFeedback.notify(res.data.message);
            // Refresh worker data
            const updatedWorker = { ...formData, user_id: res.data.user.id };
            setFormData(updatedWorker);
            setTrabalhadores(prev => prev.map(t => t.id === selectedTrabalhador.id ? { ...t, user_id: res.data.user.id } : t));
        } catch (err) {
            console.error('Erro ao gerar utilizador:', err);
            liquidFeedback.notify(err.response?.data?.error || 'Erro ao gerar utilizador');
        }
    };

    const handleGenerateOnboardingLink = async () => {
        if (!selectedTrabalhador) {
            liquidFeedback.notify('Grave a ficha primeiro.');
            return;
        }
        try {
            const res = await api.post(`/trabalhadores/${selectedTrabalhador.id}/onboarding-link`);
            const url = `${window.location.origin}/onboarding/${res.data.token}`;
            try { await navigator.clipboard.writeText(url); } catch {}
            const expira = new Date(res.data.expira_em).toLocaleString('pt-PT');
            window.prompt(
                `🔗 Link de preenchimento gerado (válido até ${expira}).\nCopiado para a área de transferência. Envie-o ao trabalhador:`,
                url
            );
        } catch (err) {
            console.error('Erro ao gerar link onboarding:', err);
            liquidFeedback.notify(err.response?.data?.error || 'Erro ao gerar link');
        }
    };

    const handleContratoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedTrabalhador) return;
        if (file.type !== 'application/pdf') { liquidFeedback.notify('Apenas PDF é aceite.'); return; }
        // Há contratos que chegam já assinados em papel. Nesses, gerar a versão
        // assinada carimbaria rubricas e assinaturas por cima das que já lá
        // estão — daí perguntar agora, e não depois de o estrago estar feito.
        const jaAssinado = await liquidFeedback.confirm(
            'Este contrato já vem assinado?\n\n'
            + 'OK — já vem assinado: a aplicação não volta a assinar e o botão '
            + '"PDF Assinado" deixa de aparecer.\n\n'
            + 'Cancelar — vem por assinar: mantém-se a assinatura pela aplicação.');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('ja_assinado', jaAssinado ? 'true' : 'false');
        try {
            await api.post(`/trabalhadores/${selectedTrabalhador.id}/contrato`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const now = new Date().toISOString();
            setFormData(prev => ({ ...prev, contrato_pdf_path: file.name, contrato_carregado_em: now, contrato_ja_assinado: jaAssinado }));
            setTrabalhadores(prev => prev.map(t => t.id === selectedTrabalhador.id ? { ...t, contrato_pdf_path: file.name, contrato_carregado_em: now, contrato_ja_assinado: jaAssinado } : t));
            liquidFeedback.notify(jaAssinado
                ? 'Contrato carregado como já assinado. A aplicação não lhe vai acrescentar assinaturas.'
                : 'Contrato carregado com sucesso.');
        } catch (err) {
            liquidFeedback.notify(err.response?.data?.error || 'Erro ao carregar contrato.');
        } finally {
            e.target.value = '';
        }
    };

    const handleContratoVer = async () => {
        if (!selectedTrabalhador) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${api.defaults.baseURL}/trabalhadores/${selectedTrabalhador.id}/contrato/pdf`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error();
            window.open(URL.createObjectURL(await res.blob()), '_blank');
        } catch {
            liquidFeedback.notify('Erro ao abrir contrato.');
        }
    };

    const handleContratoRemover = async () => {
        if (!selectedTrabalhador) return;
        if (!await liquidFeedback.confirm('Remover o contrato carregado?')) return;
        try {
            await api.delete(`/trabalhadores/${selectedTrabalhador.id}/contrato`);
            setFormData(prev => ({ ...prev, contrato_pdf_path: null, contrato_carregado_em: null }));
            setTrabalhadores(prev => prev.map(t => t.id === selectedTrabalhador.id ? { ...t, contrato_pdf_path: null, contrato_carregado_em: null } : t));
        } catch (err) {
            liquidFeedback.notify(err.response?.data?.error || 'Erro ao remover contrato.');
        }
    };

    const handleGerarDeclaracao = async () => {
        if (!selectedTrabalhador) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${api.defaults.baseURL}/trabalhadores/${selectedTrabalhador.id}/declaracao-trabalho`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Erro ao gerar declaração.');
            }
            window.open(URL.createObjectURL(await res.blob()), '_blank');
        } catch (err) {
            liquidFeedback.notify(err.message || 'Erro ao gerar declaração.');
        }
    };

    const handleCopyWhatsApp = () => {
        const docTipoMap = {
            'CC': 'Cartão de Cidadão',
            'Passaporte': 'Passaporte',
            'AutorizacaoResidencia': 'Autorização de Residência'
        };

        const localNascimento = [formData.naturalidade_distrito, formData.naturalidade_concelho, formData.naturalidade_freguesia]
            .filter(Boolean).join(', ');

        const dataNascimento = formData.data_nascimento
            ? new Date(formData.data_nascimento + 'T00:00:00').toLocaleDateString('pt-PT')
            : '';

        const texto = `Nome completo: ${formData.nome || ''}
Data de nascimento: ${dataNascimento}
Local de nascimento: ${localNascimento}
Nacionalidade: ${formData.nacionalidade || ''}
Morada: ${formData.residencia || ''}
País de residência:
Tipo de documento de identificação (Passaporte, Cartão de Cidadão ou Carta de Condução): ${docTipoMap[formData.doc_identificacao_tipo] || formData.doc_identificacao_tipo || ''}
Número do documento de identificação: ${formData.doc_identificacao_num || ''}
País emissor do documento de identificação: `;

        const doCopy = () => {
            setWhatsappCopied(true);
            setTimeout(() => setWhatsappCopied(false), 2500);
        };

        if (navigator.clipboard) {
            navigator.clipboard.writeText(texto).then(doCopy).catch(() => {
                const el = document.createElement('textarea');
                el.value = texto;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                doCopy();
            });
        } else {
            const el = document.createElement('textarea');
            el.value = texto;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            doCopy();
        }
    };

    const handleDelete = async (id) => {
        if (await liquidFeedback.confirm('Tem certeza?')) {
            try {
                await api.delete(`/trabalhadores/${id}`);
                fetchTrabalhadores();
            } catch (error) {
                console.error('Erro ao eliminar:', error);
            }
        }
    };

    // Ativar/desativar: o inativo deixa de aparecer no mapa de férias dos anos
    // seguintes (no ano da saída continua a aparecer).
    const handleToggleAtivo = async (sub) => {
        const desativar = !!sub.ativo;
        const msg = desativar
            ? `Desativar "${sub.nome}"?\n\nDeixa de aparecer no mapa de férias a partir do próximo ano e nas listas de ativos. O histórico mantém-se.`
            : `Reativar "${sub.nome}"?`;
        if (!await liquidFeedback.confirm(msg)) return;
        try {
            await api.patch(`/trabalhadores/${sub.id}/ativo`, { ativo: !desativar });
            fetchTrabalhadores();
        } catch (error) {
            liquidFeedback.notify('Erro ao alterar o estado: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDistribuirEpiHoje = async (sub) => {
        if (epiHojeAProcessar) return;
        const dataEntrega = dataHojeLisboa();
        setEpiHojeAProcessar(sub.id);
        try {
            const catalogoResponse = await api.get('/sistema-integrado/epi-catalogo');
            const episAtivos = (catalogoResponse.data || []).filter(epi => epi.ativo !== false);
            if (episAtivos.length === 0) {
                liquidFeedback.notify('Não existem EPIs ativos no catálogo para distribuir.');
                return;
            }

            const confirmar = await liquidFeedback.confirm(
                `Criar uma nova distribuição de EPI para "${sub.nome}" com a data de hoje (${new Date(`${dataEntrega}T12:00:00`).toLocaleDateString('pt-PT')})?\n\nSerão acrescentados ao histórico os ${episAtivos.length} EPIs ativos do catálogo, mesmo que já tenham sido entregues anteriormente.`
            );
            if (!confirmar) return;

            const response = await api.post('/sistema-integrado/epi-atribuicoes/lote', {
                trabalhador_id: sub.id,
                data_entrega: dataEntrega,
                observacoes: 'Distribuição completa registada na Gestão de Trabalhadores — EPI data de hoje',
                itens: episAtivos.map(epi => ({
                    epi_id: epi.id,
                    tamanho: '',
                    quantidade: 1
                }))
            });
            await fetchTrabalhadores();
            liquidFeedback.notify(
                `Nova distribuição registada: ${response.data?.entregues || episAtivos.length} EPI(s), com data de hoje. O histórico e a ficha do trabalhador foram atualizados.`
            );
        } catch (error) {
            liquidFeedback.notify('Erro ao registar a distribuição de EPI: ' + (error.response?.data?.error || error.message));
        } finally {
            setEpiHojeAProcessar(null);
        }
    };

    const handleGenerateEPI = async (sub) => {
        try {
            const response = await api.get(`/trabalhadores/epi-document/${sub.id}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `EPI_${sub.nome.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao gerar documento EPI', error);
            liquidFeedback.notify('Erro ao gerar o documento de EPI.');
        }
    };

    const handleGenerateAptidao = async (sub) => {
        try {
            const response = await api.get(`/trabalhadores/aptidao-document/${sub.id}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Aptidao_${sub.nome.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao gerar documento de Aptidão', error);
            liquidFeedback.notify('Erro ao gerar a declaração de Aptidão.');
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await api.get('/trabalhadores/template', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'template_importacao_trabalhadores.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Erro template:', error);
            liquidFeedback.notify('Erro ao baixar template');
        }
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await api.post('/trabalhadores/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            liquidFeedback.notify('Importação concluída!');
            fetchTrabalhadores();
        } catch (error) {
            console.error('Erro import:', error);
            liquidFeedback.notify('Erro ao importar');
        }
    };

    const filteredTrabalhadores = trabalhadores.filter(t => {
        const matchesSearch = t.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.empresa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.categoria_profissional?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'todos' ? true :
            filterStatus === 'ativos' ? t.ativo : !t.ativo;

        return matchesSearch && matchesStatus;
    });
    return (
        <div className="trabalhadores-page">
            {/* Top Bar Uniforme */}
            {integrado ? (acoesDestino && createPortal(<div className="top-bar">
                {!integrado && <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="users" fallback="👷" /> Gestão de Trabalhadores</h1>
                        <p>Ficheiros de pessoal e documentação</p>
                    </div>
                </div>
                }
                <div className="top-bar-actions">
                    <button className="btn-secondary" onClick={handleDownloadTemplate} style={{ backgroundColor: '#edf2f7', color: '#4a5568' }}>
                        <OrcamentoIcon name="document" fallback="📄" /> Template Excel
                    </button>
                    <input
                        type="file"
                        id="import-excel-worker"
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls, .csv"
                        onChange={handleImportExcel}
                    />
                    <button className="btn-secondary" onClick={() => document.getElementById('import-excel-worker').click()}>
                        <OrcamentoIcon name="upload" fallback="📥" /> Importar Excel
                    </button>
                    <button className="btn-primary" onClick={() => handleOpenModal()}>
                        + Novo Trabalhador
                    </button>
                </div>
            </div>, acoesDestino)) : (<div className="top-bar">
                {!integrado && <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="users" fallback="👷" /> Gestão de Trabalhadores</h1>
                        <p>Ficheiros de pessoal e documentação</p>
                    </div>
                </div>
                }
                <div className="top-bar-actions">
                    <button className="btn-secondary" onClick={handleDownloadTemplate} style={{ backgroundColor: '#edf2f7', color: '#4a5568' }}>
                        <OrcamentoIcon name="document" fallback="📄" /> Template Excel
                    </button>
                    <input
                        type="file"
                        id="import-excel-worker"
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls, .csv"
                        onChange={handleImportExcel}
                    />
                    <button className="btn-secondary" onClick={() => document.getElementById('import-excel-worker').click()}>
                        <OrcamentoIcon name="upload" fallback="📥" /> Importar Excel
                    </button>
                    <button className="btn-primary" onClick={() => handleOpenModal()}>
                        + Novo Trabalhador
                    </button>
                </div>
            </div>)}

            {/* Conteúdo da Página */}
            <div className="page-content">
                <div className="shared-search">
                    <input
                        type="text"
                        placeholder="Pesquisar por nome, categoria ou empresa..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="liquid-list-toolbar" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="shared-subnav" style={{ marginBottom: 0 }}>
                        <button className={`shared-subnav-btn ${filterStatus === 'todos' ? 'active' : ''}`} onClick={() => setFilterStatus('todos')}>Todos</button>
                        <button className={`shared-subnav-btn ${filterStatus === 'ativos' ? 'active' : ''}`} onClick={() => setFilterStatus('ativos')}>Ativos</button>
                        <button className={`shared-subnav-btn ${filterStatus === 'inativos' ? 'active' : ''}`} onClick={() => setFilterStatus('inativos')}>Inativos</button>
                    </div>

                    {/* View Toggler */}
                    <div className="liquid-view-toggle" role="group" aria-label="Modo de visualização" style={{ display: 'flex', background: '#e2e8f0', padding: '4px', borderRadius: '8px', gap: '4px' }}>
                        <button
                            aria-pressed={viewMode === 'grid'}
                            onClick={() => setViewMode('grid')}
                            style={{
                                background: viewMode === 'grid' ? 'white' : 'transparent',
                                border: 'none',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                            title="Vista de Cartões"
                        >
                            <OrcamentoIcon name="dashboard" fallback="🏗️" /> Grelha
                        </button>
                        <button
                            aria-pressed={viewMode === 'list'}
                            onClick={() => setViewMode('list')}
                            style={{
                                background: viewMode === 'list' ? 'white' : 'transparent',
                                border: 'none',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                            title="Vista de Lista"
                        >
                            <OrcamentoIcon name="list" fallback="📋" /> Lista
                        </button>
                    </div>
                </div>

                {filteredTrabalhadores.length === 0 ? (
                    <div className="trabalhadores-table-container">
                        <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                            {searchTerm ? 'Nenhum trabalhador encontrado para a pesquisa.' : 'Nenhum trabalhador registado.'}
                        </div>
                    </div>
                ) : (
                    <>
                        {viewMode === 'list' ? (
                            <div className="trabalhadores-table-container">
                                <table className="standard-table trabalhadores-table">
                                    <thead>
                                        <tr>
                                            <th>Nº</th>
                                            <th>Nome</th>
                                            <th>Categoria</th>
                                            <th>Empresa</th>
                                            <th>Validade Apto</th>
                                            <th>Estado</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTrabalhadores.map(sub => (
                                            <tr key={sub.id} className={sub.ativo ? undefined : 'linha-inativo'}
                                                onClick={() => handleOpenModal(sub)}>
                                                <td>{sub.numero_trabalhador}</td>
                                                <td style={{ fontWeight: '500' }}>{sub.nome}</td>
                                                <td>{sub.categoria_profissional}</td>
                                                <td>{sub.empresa}</td>
                                                <td>
                                                    {sub.aptidao_validade && (
                                                        <span style={{
                                                            color: new Date(sub.aptidao_validade) < new Date() ? 'red' : 'green'
                                                        }}>
                                                            {new Date(sub.aptidao_validade).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleToggleAtivo(sub); }}
                                                        title={sub.ativo ? 'Clique para desativar (sai do mapa de férias dos anos seguintes)' : 'Clique para reativar'}
                                                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
                                                    >
                                                        <span className={`status-dot ${sub.ativo ? 'active' : 'inactive'}`}></span>
                                                        {sub.ativo ? 'Ativo' : 'Inativo'}
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="actions-cell">
                                                        <button className="btn-epi" onClick={(e) => { e.stopPropagation(); handleToggleAtivo(sub); }} title={sub.ativo ? 'Desativar trabalhador' : 'Reativar trabalhador'}><OrcamentoIcon name={sub.ativo ? 'ban' : 'check'} fallback={sub.ativo ? '🚫' : '✅'} /></button>
                                                        <button className="btn-epi" onClick={(e) => { e.stopPropagation(); handleGenerateAptidao(sub); }} title="Declaração de Aptidão"><OrcamentoIcon name="forms" fallback="🩺" /></button>
                                                        <button className="btn-epi" onClick={(e) => { e.stopPropagation(); handleGenerateEPI(sub); }} title="Ficha de posse de EPI (PDF)"><OrcamentoIcon name="shield" fallback="🛡️" /></button>
                                                        <button className="btn-epi btn-epi-today" disabled={epiHojeAProcessar === sub.id} aria-busy={epiHojeAProcessar === sub.id}
                                                            onClick={(e) => { e.stopPropagation(); handleDistribuirEpiHoje(sub); }}
                                                            title="Criar nova distribuição de todos os EPIs ativos com data de hoje">
                                                            <OrcamentoIcon name={epiHojeAProcessar === sub.id ? 'refresh' : 'calendar'} fallback={epiHojeAProcessar === sub.id ? '⏳' : '📅'} />
                                                            <span className="btn-epi-today-label">{epiHojeAProcessar === sub.id ? 'A registar…' : 'EPI hoje'}</span>
                                                        </button>
                                                        <button className="btn-edit" onClick={(e) => { e.stopPropagation(); handleOpenModal(sub); }} title="Editar"><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                                        <button className="btn-delete" onClick={(e) => { e.stopPropagation(); handleDelete(sub.id); }} title="Eliminar"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="trabalhadores-grid">
                                {filteredTrabalhadores.map(sub => (
                                    <div
                                        key={sub.id}
                                        onClick={() => handleOpenModal(sub)}
                                        className="trabalhador-card"
                                    >
                                        <div className="card-header">
                                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                <div style={{
                                                    width: '50px', height: '50px', borderRadius: '50%',
                                                    background: '#edf2f7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '20px', overflow: 'hidden'
                                                }}>
                                                    {sub.foto_url ? (
                                                        <img src={`${api.defaults.baseURL.split('/api')[0]}${sub.foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        '👤'
                                                    )}
                                                </div>
                                                <div className="card-body">
                                                    <h4>{sub.nome}</h4>
                                                    <p>{sub.categoria_profissional}</p>
                                                </div>
                                            </div>
                                            <span className={`status-dot ${sub.ativo ? 'active' : 'inactive'}`} title={sub.ativo ? 'Ativo' : 'Inativo'}></span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.9rem', color: '#4a5568', marginTop: '10px' }}>
                                            <div><OrcamentoIcon name="building" fallback="🏢" /> {sub.empresa}</div>
                                            <div><LiquidText>🆔 </LiquidText>{sub.numero_trabalhador || 'N/A'}</div>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                {sub.aptidao_validade ? (
                                                    <span style={{
                                                        color: new Date(sub.aptidao_validade) < new Date() ? 'red' : 'green',
                                                        fontWeight: '500'
                                                    }}>
                                                        <OrcamentoIcon name="forms" fallback="🩺" /> Apto até: {new Date(sub.aptidao_validade).toLocaleDateString()}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#a0aec0' }}><OrcamentoIcon name="forms" fallback="🩺" /> Sem data de aptidão</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="trabalhador-footer">
                                            <div className="actions-cell">
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleToggleAtivo(sub); }} title={sub.ativo ? 'Desativar trabalhador' : 'Reativar trabalhador'}><OrcamentoIcon name={sub.ativo ? 'ban' : 'check'} fallback={sub.ativo ? '🚫' : '✅'} /></button>
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleGenerateAptidao(sub); }} title="Declaração de Aptidão"><OrcamentoIcon name="forms" fallback="🩺" /></button>
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleGenerateEPI(sub); }} title="Ficha de posse de EPI (PDF)"><OrcamentoIcon name="shield" fallback="🛡️" /></button>
                                                <button className="action-btn btn-epi-today" disabled={epiHojeAProcessar === sub.id} aria-busy={epiHojeAProcessar === sub.id}
                                                    onClick={(e) => { e.stopPropagation(); handleDistribuirEpiHoje(sub); }}
                                                    title="Criar nova distribuição de todos os EPIs ativos com data de hoje">
                                                    <OrcamentoIcon name={epiHojeAProcessar === sub.id ? 'refresh' : 'calendar'} fallback={epiHojeAProcessar === sub.id ? '⏳' : '📅'} />
                                                    <span className="btn-epi-today-label">{epiHojeAProcessar === sub.id ? 'A registar…' : 'EPI hoje'}</span>
                                                </button>
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleOpenModal(sub); }} title="Editar"><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleDelete(sub.id); }} title="Eliminar" style={{ color: '#e53e3e', borderColor: '#fed7d7', background: '#fff5f5' }}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {showModal && (
                    <LiquidDialog onClose={() => setShowModal(false)} className="worker-modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="worker-modal" onClick={e => e.stopPropagation()}>
                            <div className="worker-modal-header">
                                {/* O nome à frente do título: o modal tem cinco separadores e,
                                    a meio do preenchimento, deixava de haver no ecrã qualquer
                                    indicação de quem se está a editar. */}
                                <h2>
                                    {selectedTrabalhador ? 'Editar Ficha' : 'Novo Trabalhador'}
                                    {selectedTrabalhador && (formData.nome || selectedTrabalhador.nome) && (
                                        <span className="worker-modal-nome">
                                            {formData.nome || selectedTrabalhador.nome}
                                        </span>
                                    )}
                                </h2>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button className="close-btn" aria-label="Fechar" onClick={() => setShowModal(false)}><OrcamentoIcon name="close" fallback="×" /></button>
                                </div>
                            </div>

                            {/* Com sete separadores a barra deixou de caber em ecrãs
                                estreitos (a partir de uma janela de ~1180px) e quebrava
                                para duas linhas. Aqui desliza na horizontal: o
                                whiteSpace impede que os rótulos partam, e o
                                overflowX dá a barra de deslocamento. Acima disso
                                fica tudo igual. */}
                            <div className="worker-modal-tabs" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                <button
                                    className={`worker-tab-btn ${activeTab === 'pessoais' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('pessoais')}
                                >
                                    <OrcamentoIcon name="users" fallback="👤" /> Dados Pessoais
                                </button>
                                <button
                                    className={`worker-tab-btn ${activeTab === 'identificacao' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('identificacao')}
                                >
                                    <OrcamentoIcon name="document" fallback="🪪" /> Identificação
                                </button>
                                <button
                                    className={`worker-tab-btn ${activeTab === 'sst' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('sst')}
                                >
                                    <OrcamentoIcon name="forms" fallback="🩺" /> Saúde & Segurança
                                </button>
                                <button
                                    className={`worker-tab-btn ${activeTab === 'formacao' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('formacao')}
                                >
                                    <OrcamentoIcon name="document" fallback="🎓" /> Formação
                                </button>

                                <button
                                    className={`worker-tab-btn ${activeTab === 'documentos' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('documentos')}
                                >
                                    <OrcamentoIcon name="document" fallback="📄" /> Documentos
                                </button>
                                {/* Rótulo curto de propósito: com sete separadores o modal
                                    de 1150px fica quase cheio, e num ecrã mais estreito a
                                    barra já tem de deslizar. */}
                                <button
                                    className={`worker-tab-btn ${activeTab === 'historico' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('historico')}
                                >
                                    <OrcamentoIcon name="document" fallback="🗂️" /> Histórico{nHistorico > 0 ? ` (${nHistorico})` : ''}
                                </button>
                                <button
                                    className={`worker-tab-btn ${activeTab === 'cartoes' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('cartoes')}
                                >
                                    <OrcamentoIcon name="card" fallback="💳" /> Cartões Associados
                                </button>
                            </div>

                            <form ref={workerContentRef} onSubmit={handleSubmit} className="worker-modal-content">
                                {activeTab === 'pessoais' && (
                                    <>
                                        {/* Secção de Foto de Perfil */}
                                        <div className="worker-photo-section">
                                            <div className="worker-photo-container">
                                                {formData.foto_url ? (
                                                    <img src={`${api.defaults.baseURL.split('/api')[0]}${formData.foto_url}`} alt="Avatar" className="worker-avatar-preview" />
                                                ) : (
                                                    <div className="worker-avatar-placeholder">
                                                        <span><OrcamentoIcon name="users" fallback="👤" /></span>
                                                    </div>
                                                )}
                                                <div className="photo-actions">
                                                    <label className="photo-btn upload-btn">
                                                        <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                                                        <OrcamentoIcon name="document" fallback="📁" /> Carregar Foto
                                                    </label>
                                                    <button type="button" className="photo-btn capture-btn" onClick={startCamera}>
                                                        <OrcamentoIcon name="camera" fallback="📸" /> Tirar Foto
                                                    </button>
                                                </div>
                                                {uploadingPhoto && <div className="photo-loader">A carregar...</div>}
                                            </div>
                                        </div>

                                        <div className="worker-form-grid">
                                            <div className="worker-form-field" style={{ gridColumn: 'span 3' }}>
                                                <label>Nome Completo *</label>
                                                <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} required />
                                            </div>

                                            <div className="worker-form-field">
                                                <label>N.º do trabalhador</label>
                                                <input
                                                    type="text"
                                                    name="numero_trabalhador"
                                                    value={selectedTrabalhador ? (formData.numero_trabalhador || '') : ''}
                                                    placeholder="Automático"
                                                    readOnly
                                                    disabled
                                                    title="Atribuído automaticamente"
                                                    style={{ background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
                                                />
                                            </div>
                                            <div className="worker-form-field" style={{ display: 'flex', alignItems: 'center', marginTop: '30px' }}>
                                                <input
                                                    type="checkbox"
                                                    name="apto_para_obras"
                                                    checked={formData.apto_para_obras !== false} // Default true
                                                    onChange={(e) => setFormData({ ...formData, apto_para_obras: e.target.checked })}
                                                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                                                />
                                                <label style={{ marginBottom: 0, cursor: 'pointer' }} onClick={() => setFormData({ ...formData, apto_para_obras: !formData.apto_para_obras })}>
                                                    Apto para Obras (disponível para "Adicionar Todos")
                                                </label>
                                            </div>

                                            <div className="worker-form-field">
                                                <label>Estado do trabalhador</label>
                                                <select
                                                    name="ativo"
                                                    value={formData.ativo === false ? 'false' : 'true'}
                                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.value === 'true' })}
                                                >
                                                    <option value="true">Ativo</option>
                                                    <option value="false">Inativo (saiu da empresa)</option>
                                                </select>
                                                <small style={{ color: '#718096', fontSize: '0.75rem' }}>
                                                    Inativo: sai do mapa de férias a partir do ano seguinte ao da saída.
                                                </small>
                                            </div>

                                            <div className="worker-form-field" style={{ gridColumn: 'span 2' }}>
                                                <label>Residência</label>
                                                <input type="text" name="residencia" value={formData.residencia} onChange={handleInputChange} />
                                            </div>
                                            <div className="worker-form-field">
                                                <label>Empresa</label>
                                                <input type="text" name="empresa" value={formData.empresa} onChange={handleInputChange} />
                                            </div>

                                            <div className="worker-form-field">
                                                <label>Código Postal</label>
                                                <input type="text" name="codigo_postal" value={formData.codigo_postal || ''} onChange={handleInputChange} placeholder="Ex: 1234-123" />
                                            </div>
                                            <div className="worker-form-field" style={{ gridColumn: 'span 2' }}>
                                                <label>Localidade</label>
                                                <input type="text" name="localidade" value={formData.localidade || ''} onChange={handleInputChange} />
                                            </div>

                                            <div className="worker-form-field">
                                                <label>Telemóvel</label>
                                                <input type="tel" name="telefone" value={formData.telefone} onChange={handleInputChange} placeholder="Ex: 912345678" />
                                            </div>
                                            <div className="worker-form-field">
                                                <label>Telefone 1</label>
                                                <input type="tel" name="telefone_fixo" value={formData.telefone_fixo || ''} onChange={handleInputChange} />
                                            </div>
                                            <div className="worker-form-field">
                                                <label>Telefone 2</label>
                                                <input type="tel" name="telefone_alt" value={formData.telefone_alt || ''} onChange={handleInputChange} />
                                            </div>
                                            <div className="worker-form-field" style={{ gridColumn: 'span 3' }}>
                                                <label>Email</label>
                                                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Ex: nome@email.com" />
                                            </div>

                                            <div data-liquid-surface="true" className="worker-form-section worker-access-card full-width" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#2b6cb0' }}><LiquidText>📱 Plataforma Mobile</LiquidText></h3>
                                                    <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                                        <LiquidText>{formData.user_id
                                                            ? '✅ Este trabalhador já possui um utilizador associado.'
                                                            : 'Este trabalhador ainda não tem conta para aceder à App.'}</LiquidText>
                                                    </p>
                                                </div>
                                                {selectedTrabalhador && !formData.user_id && (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        onClick={handleGenerateUser}
                                                        style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                                                    ><LiquidText>
                                                        ✨ Gerar Utilizador
                                                    </LiquidText></button>
                                                )}
                                            </div>

                                            <div data-liquid-surface="true" className="worker-form-section worker-access-card full-width" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#2b6cb0' }}><LiquidText>🔗 Preenchimento pelo Trabalhador</LiquidText></h3>
                                                    <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                                        <LiquidText>{formData.onboarding_completado_em
                                                            ? `✅ Trabalhador concluiu o preenchimento em ${new Date(formData.onboarding_completado_em).toLocaleDateString('pt-PT')}.`
                                                            : (formData.contrato_pdf_path
                                                                ? 'Gere um link único (válido 7 dias) para o trabalhador preencher a ficha e assinar o contrato.'
                                                                : '⚠️ Carregue primeiro o contrato no separador "Documentos" antes de gerar o link.')}</LiquidText>
                                                    </p>
                                                </div>
                                                {selectedTrabalhador && (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        onClick={handleGenerateOnboardingLink}
                                                        disabled={!formData.contrato_pdf_path}
                                                        style={{ padding: '5px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0, opacity: formData.contrato_pdf_path ? 1 : 0.5, cursor: formData.contrato_pdf_path ? 'pointer' : 'not-allowed' }}
                                                    ><LiquidText>
                                                        🔗 Gerar
                                                    </LiquidText></button>
                                                )}
                                            </div>

                                            <div className="worker-form-field">
                                                <label>Contacto de Emergência</label>
                                                <input type="text" name="contacto_emergencia" value={formData.contacto_emergencia} onChange={handleInputChange} placeholder="Nome e telefone" />
                                            </div>

                                            <div className="worker-form-field" style={{ gridColumn: 'span 3' }}>
                                                <label>Categoria Profissional</label>
                                                <input type="text" name="categoria_profissional" value={formData.categoria_profissional} onChange={handleInputChange} />
                                            </div>

                                            {/* Assinatura e rubrica lado a lado, metade da linha cada.
                                                A grelha exterior é de 3 colunas, que não parte ao meio —
                                                daí este contentor ocupar a linha toda e dividir-se por dentro. */}
                                            <div style={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                            <div className="worker-form-field">
                                                <label>Assinatura Digital (JPEG/PNG)</label>
                                                <div className="signature-upload">

                                                    {formData.assinatura_url && (
                                                        <div className="signature-preview">
                                                            <img src={formData.assinatura_url} alt="Assinatura" style={{ maxHeight: '80px', border: '1px solid #ddd', marginBottom: '10px' }} />
                                                            <button aria-label="Apagar" type="button" className="btn-icon" onClick={() => setFormData({ ...formData, assinatura_url: '' })}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                        <button type="button" className="btn-secondary" onClick={() => abrirAssinatura('assinatura_url')}><LiquidText>🖊️ Assinar no Ecrã (iPad/Tablet)</LiquidText></button>
                                                        <span style={{ fontSize: '0.8rem', color: '#718096' }}>ou</span>
                                                        <label className="btn-secondary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                                            <OrcamentoIcon name="upload" fallback="📤" /> Carregar Imagem
                                                            <input type="file" accept="image/*" onChange={e => handleFileChange(e, 'assinatura_url')} style={{ display: 'none' }} />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Rubrica: o traço curto para visar páginas e anexos, a par
                                                da assinatura por extenso. Mesmo quadro de desenho.
                                                Vem preenchida com a assinatura até ser desenhada à parte. */}
                                            <div className="worker-form-field">
                                                <label>
                                                    Rubrica (JPEG/PNG)
                                                    {formData.rubrica_url && formData.rubrica_url === formData.assinatura_url && (
                                                        <span style={{ fontWeight: 400, color: '#718096', fontSize: '0.78rem', marginLeft: 6 }}>
                                                            — igual à assinatura
                                                        </span>
                                                    )}
                                                </label>
                                                <div className="signature-upload">
                                                    {formData.rubrica_url && (
                                                        <div className="signature-preview">
                                                            <img src={formData.rubrica_url} alt="Rubrica" style={{ maxHeight: '80px', border: '1px solid #ddd', marginBottom: '10px' }} />
                                                            <button aria-label="Apagar" type="button" className="btn-icon" onClick={() => setFormData({ ...formData, rubrica_url: '' })}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                        <button type="button" className="btn-secondary" onClick={() => abrirAssinatura('rubrica_url')}><LiquidText>🖊️ Rubricar no Ecrã (iPad/Tablet)</LiquidText></button>
                                                        <span style={{ fontSize: '0.8rem', color: '#718096' }}>ou</span>
                                                        <label className="btn-secondary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                                            <OrcamentoIcon name="upload" fallback="📤" /> Carregar Imagem
                                                            <input type="file" accept="image/*" onChange={e => handleFileChange(e, 'rubrica_url')} style={{ display: 'none' }} />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {activeTab === 'identificacao' && (
                                    <div className="worker-form-grid">
                                        <div className="worker-form-section full-width">
                                            <h3><OrcamentoIcon name="document" fallback="🪪" /> Documentos Civis</h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Tipo Docum. Ident</label>
                                            <select name="doc_identificacao_tipo" value={formData.doc_identificacao_tipo} onChange={handleInputChange}>
                                                <option value="CC">Cartão Cidadão</option>
                                                <option value="Passaporte">Passaporte</option>
                                                <option value="AutorizacaoResidencia">Autorização Residência</option>
                                            </select>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Nº Doc. Id.</label>
                                            <input type="text" name="doc_identificacao_num" value={formData.doc_identificacao_num} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data de Emissão Doc. Id.</label>
                                            <input type="date" name="doc_identificacao_emissao" value={formData.doc_identificacao_emissao || ''} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data de validade Doc. Id.</label>
                                            <input type="date" name="doc_identificacao_validade" value={formData.doc_identificacao_validade} onChange={handleInputChange} />
                                        </div>

                                        <div className="worker-form-field">
                                            <label>Nacionalidade</label>
                                            <input type="text" name="nacionalidade" value={formData.nacionalidade} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data Nascimento</label>
                                            <input type="date" name="data_nascimento" value={formData.data_nascimento} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Nº. Ident. Fiscal (NIF)</label>
                                            <input type="text" name="nif" value={formData.nif} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Nº. Segurança Social (NISS)</label>
                                            <input type="text" name="niss" value={formData.niss} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Habilitações Literárias</label>
                                            <select name="habilitacoes_literarias" value={formData.habilitacoes_literarias || ''} onChange={handleInputChange}>
                                                <option value="">Selecione...</option>
                                                <option value="Sem escolaridade">Sem escolaridade</option>
                                                <option value="1º Ciclo (4ª classe)">1º Ciclo (4ª classe)</option>
                                                <option value="2º Ciclo (6º ano)">2º Ciclo (6º ano)</option>
                                                <option value="3º Ciclo (9º ano)">3º Ciclo (9º ano)</option>
                                                <option value="Ensino Secundário">Ensino Secundário</option>
                                                <option value="Curso Profissional">Curso Profissional</option>
                                                <option value="Bacharelato">Bacharelato</option>
                                                <option value="Licenciatura">Licenciatura</option>
                                                <option value="Mestrado">Mestrado</option>
                                                <option value="Doutoramento">Doutoramento</option>
                                            </select>
                                        </div>
                                        <div className="worker-form-field" style={{ gridColumn: 'span 3' }}>
                                            <label>IBAN</label>
                                            <input type="text" name="iban" value={formData.iban || ''} onChange={handleInputChange} placeholder="PT50 0000 0000 0000 0000 0000 0" />
                                        </div>

                                        <div className="worker-form-section full-width" style={{ marginTop: '20px' }}>
                                            <h3><LiquidText>📑 Contrato de Trabalho</LiquidText></h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data de Início do Contrato</label>
                                            <input type="date" name="contrato_data_inicio" value={formData.contrato_data_inicio || ''} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data de Fim do Contrato (se a termo)</label>
                                            <input type="date" name="contrato_data_fim" value={formData.contrato_data_fim || ''} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field" style={{ justifyContent: 'flex-end' }}>
                                            <small style={{ color: '#718096' }}>Se a data de fim já passou (ou estiver vazia), o trabalhador é considerado efetivo e na declaração consta apenas a data de início.</small>
                                        </div>
                                        {/* Estes dois só servem os alertas de renovação. A duração inicial
                                            não muda com as renovações — ao contrário da data de fim — e é
                                            dela que sai o termo máximo permitido por lei. */}
                                        <div className="worker-form-field">
                                            <label>Duração inicial (dias)</label>
                                            <input type="number" min="1" name="contrato_duracao_dias"
                                                value={formData.contrato_duracao_dias || ''}
                                                onChange={handleInputChange}
                                                placeholder="ex: 180" />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Pré-aviso de não renovação (dias)</label>
                                            <input type="number" min="0" name="contrato_preaviso_dias"
                                                value={formData.contrato_preaviso_dias ?? 15}
                                                onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field" style={{ justifyContent: 'flex-end' }}>
                                            <small style={{ color: '#718096' }}>Usados apenas pelos alertas de renovação, quando ligados na aba Documentos.</small>
                                        </div>

                                        <div className="worker-form-section full-width" style={{ marginTop: '20px' }}>
                                            <h3><LiquidText>📍 Naturalidade (Distrito - Concelho - Freguesia)</LiquidText></h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Distrito</label>
                                            <input type="text" name="naturalidade_distrito" value={formData.naturalidade_distrito || ''} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Concelho</label>
                                            <input type="text" name="naturalidade_concelho" value={formData.naturalidade_concelho || ''} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Freguesia</label>
                                            <input type="text" name="naturalidade_freguesia" value={formData.naturalidade_freguesia || ''} onChange={handleInputChange} />
                                        </div>

                                        <div className="worker-form-section full-width" style={{ marginTop: '20px' }}>
                                            <h3><LiquidText>🌍 Trabalhadores Estrangeiros</LiquidText></h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Nº. Visto</label>
                                            <input type="text" name="estrangeiro_num_visto" value={formData.estrangeiro_num_visto} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Validade Visto</label>
                                            <input type="date" name="estrangeiro_validade_visto" value={formData.estrangeiro_validade_visto} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field" style={{ flexDirection: 'row', alignItems: 'center', marginTop: '25px' }}>
                                            <input type="checkbox" name="estrangeiro_contrato_act" checked={formData.estrangeiro_contrato_act} onChange={handleInputChange} />
                                            <label style={{ marginBottom: 0 }}>Contrato de trabalho comunicado à ACT (Sim/Não)</label>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'sst' && (
                                    <div className="worker-form-grid">
                                        <div className="worker-form-section full-width">
                                            <h3><LiquidText>🏥 Aptidão Médica</LiquidText></h3>
                                        </div>

                                        {/* O documento em si. É a mesma evidência que está em
                                            Req. Legais > Evidências — aqui vista do lado da pessoa,
                                            que é como a auditora a vai pedir. */}
                                        <div className="worker-form-field full-width">
                                            {evidencias.filter(e => e.categoria === 'fichas_aptidao').length === 0 ? (
                                                externo ? (
                                                    <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 14px', fontSize: '0.86rem', color: '#475569' }}>
                                                        Técnico externo ({empresaTrab}) — a ficha de aptidão é da responsabilidade da entidade empregadora.
                                                        {' '}Os campos abaixo são o registo do que ela informou, não um documento nosso: enquanto a validade estiver preenchida, o Painel de Validades e os lembretes continuam a contar este prazo.
                                                    </div>
                                                ) : (
                                                    <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', fontSize: '0.86rem', color: '#92400e' }}>
                                                        Sem ficha de aptidão carregada. Os campos abaixo estão preenchidos à mão e não têm documento que os sustente.
                                                    </div>
                                                )
                                            ) : evidencias.filter(e => e.categoria === 'fichas_aptidao').map(ev => {
                                                /* Só a ficha em vigor. As substituídas já não vêm do
                                                   servidor: estão na aba Histórico. Uma arquivada
                                                   continua aqui, cinzenta e sem alerta — arquivar é outro
                                                   estado (ex-colaborador), não uma substituição. */
                                                const antiga = ev.estado === 'arquivado';
                                                const cor = ev.estado === 'arquivado' ? { bg: '#f8fafc', bd: '#e2e8f0', fg: '#475569', txt: 'ARQUIVADA' }
                                                    : ev.estado === 'expirado' ? { bg: '#fee2e2', bd: '#fca5a5', fg: '#991b1b', txt: 'EXPIRADO' }
                                                    : ev.estado === 'a_expirar' ? { bg: '#fef3c7', bd: '#fcd34d', fg: '#92400e', txt: 'A EXPIRAR' }
                                                        : ev.estado === 'valido' ? { bg: '#dcfce7', bd: '#86efac', fg: '#166534', txt: 'VÁLIDA' }
                                                            : { bg: '#f1f5f9', bd: '#cbd5e1', fg: '#475569', txt: 'SEM VALIDADE' };
                                                return (
                                                    <div key={ev.id} className="worker-medical-notice" data-liquid-notice="true" data-liquid-tone={liquidTone(cor.bg)} style={{ background: cor.bg, border: `1px solid ${cor.bd}`, borderRadius: 8, padding: antiga ? '8px 14px' : '12px 14px', marginBottom: 8 }}>
                                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <span data-liquid-badge="true" data-liquid-tone={liquidTone(cor.bg)} style={{ background: antiga ? '#94a3b8' : cor.fg, color: '#fff', padding: '3px 10px', borderRadius: 5, fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.04em' }}>{cor.txt}</span>
                                                            <span style={{ fontSize: antiga ? '0.84rem' : '0.9rem', color: cor.fg, fontWeight: antiga ? 500 : 600 }}>
                                                                Exame de {ev.data_documento ? new Date(ev.data_documento).toLocaleDateString('pt-PT') : '—'}
                                                                {ev.validade && <> · válida até {new Date(ev.validade).toLocaleDateString('pt-PT')}</>}
                                                                {ev.estado === 'a_expirar' && ev.dias != null && <> · faltam {ev.dias} dias</>}
                                                            </span>
                                                            <div style={{ flex: 1 }} />
                                                            <button type="button" className="btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                                onClick={() => abrirEvidencia(ev)}>
                                                                <OrcamentoIcon name="document" fallback="📄" /> Abrir ficha
                                                            </button>
                                                        </div>
                                                        {ev.entidade && <div style={{ fontSize: '0.78rem', color: cor.fg, marginTop: 6, opacity: 0.85 }}>{ev.entidade}</div>}
                                                        {ev.divergencias?.length > 0 && (
                                                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${cor.bd}`, fontSize: '0.8rem', color: '#92400e' }}>
                                                                <strong>Não bate certo com os campos abaixo:</strong> {ev.divergencias.join('; ')}.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="worker-form-field">
                                            <label>Data do Exame de Saúde</label>
                                            <input type="date" name="aptidao_data_exame" value={formData.aptidao_data_exame} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Validade</label>
                                            <input type="date" name="aptidao_validade" value={formData.aptidao_validade} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Resultado do exame</label>
                                            <select name="aptidao_resultado" value={formData.aptidao_resultado} onChange={handleInputChange}>
                                                <option value="">Selecione...</option>
                                                <option value="Apto">Apto</option>
                                                <option value="Apto Condicional">Apto Condicional</option>
                                                <option value="Inapto">Inapto</option>
                                            </select>
                                        </div>

                                        {/* Avaliação de riscos do posto deste trabalhador e a
                                            respetiva tomada de conhecimento assinada. O documento
                                            é o mesmo para todos os que fazem o mesmo trabalho —
                                            o que é pessoal é a assinatura. */}
                                        {evidencias.filter(e => e.origem === 'aplicavel').map(ev => (
                                            <div key={ev.id} className="worker-form-field full-width" style={{ marginTop: 4 }}>
                                                <div data-liquid-surface="true" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', color: '#64748b', textTransform: 'uppercase' }}>
                                                            Avaliação de riscos · {ev.contexto}
                                                        </span>
                                                        <span style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 600 }}><LiquidText>{ev.titulo}</LiquidText></span>
                                                        <div style={{ flex: 1 }} />
                                                        <button type="button" className="btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                            onClick={() => abrirEvidencia(ev)}><OrcamentoIcon name="document" fallback="📄" /> Abrir</button>
                                                    </div>
                                                    <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px solid #e2e8f0', fontSize: '0.84rem' }}>
                                                        {ev.comunicacao_assinada ? (
                                                            <span style={{ color: '#166534' }}><LiquidText>
                                                                ✓ Comunicação de Perigos e Riscos assinada
                                                                </LiquidText>{ev.comunicacao_data && <> em {new Date(ev.comunicacao_data).toLocaleDateString('pt-PT')}</>}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#991b1b', fontWeight: 600 }}><LiquidText>
                                                                ✗ Comunicação de Perigos e Riscos por assinar — a avaliação existe, falta a tomada de conhecimento deste trabalhador
                                                            </LiquidText></span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="worker-form-section full-width" style={{ marginTop: '20px' }}>
                                            <h3><OrcamentoIcon name="shield" fallback="🛡️" /> Segurança e EPIs</h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Trabalhador habilit. p/ conduzir equipam. (Sim/Não/Não Aplicável)</label>
                                            <select name="habilitacao_equipamentos" value={formData.habilitacao_equipamentos} onChange={handleInputChange}>
                                                <option value="Não">Não</option>
                                                <option value="Sim">Sim</option>
                                                <option value="N/A">Não Aplicável</option>
                                            </select>
                                        </div>

                                        <div className="worker-form-field" style={{ flexDirection: 'row', alignItems: 'center', marginTop: '25px' }}>
                                            <input type="checkbox" name="habilitado_conduzir" checked={formData.habilitado_conduzir} onChange={handleInputChange} />
                                            <label style={{ marginBottom: 0 }}>Habilitado p/ conduzir viatura?</label>
                                        </div>

                                        <div className="worker-form-field" style={{ flexDirection: 'row', alignItems: 'center', marginTop: '25px' }}>
                                            <input type="checkbox" name="epi_entregue" checked={formData.epi_entregue} onChange={handleInputChange} />
                                            <label style={{ marginBottom: 0 }}>EPIs Entregues? (Sim/Não)</label>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data última entrega</label>
                                            <input type="date" name="epi_data_entrega" value={formData.epi_data_entrega} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'formacao' && (
                                    <div className="worker-form-grid">
                                        {/* Formação efetivamente frequentada, com certificado e
                                            eficácia. Vem do módulo de formação do Sistema
                                            Integrado — é o mesmo registo, visto do lado da pessoa. */}
                                        <div className="worker-form-section full-width">
                                            <h3><OrcamentoIcon name="document" fallback="🎓" /> Ações frequentadas</h3>
                                        </div>
                                        <div className="worker-form-field full-width">
                                            {formacaoTrab.length === 0 ? (
                                                <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 14px', fontSize: '0.86rem', color: '#475569' }}>
                                                    Sem formação registada para este trabalhador no Sistema Integrado.
                                                </div>
                                            ) : (
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table className="standard-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>Ação</th>
                                                                <th>Data</th>
                                                                <th style={{ textAlign: 'right' }}>Horas</th>
                                                                <th>Entidade</th>
                                                                <th>Certificado</th>
                                                                <th>Eficácia</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {formacaoTrab.map((f, i) => {
                                                                const temCert = String(f.certificado || '').toLowerCase() === 'sim';
                                                                const ef = f.eficacia && f.eficacia !== '-';
                                                                return (
                                                                    <tr key={`${f.id}-${i}`}>
                                                                        <td style={{ fontWeight: 600 }}>{f.designacao}</td>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>{(f.data_realizacao || '—').split('\n')[0]}</td>
                                                                        <td style={{ textAlign: 'right' }}>{f.duracao_horas ?? '—'}</td>
                                                                        <td>{(f.entidade_formadora || '—').replace(/\n/g, ' ')}</td>
                                                                        <td>
                                                                            <span data-liquid-badge="true" data-liquid-tone={temCert ? 'success' : 'danger'} style={{ padding: '2px 9px', borderRadius: 10, fontSize: '0.76rem', fontWeight: 600, background: temCert ? '#dcfce7' : '#fee2e2', color: temCert ? '#166534' : '#991b1b' }}>
                                                                                {temCert ? 'Sim' : 'Não'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span data-liquid-badge="true" data-liquid-tone={ef ? 'success' : 'warning'} style={{ padding: '2px 9px', borderRadius: 10, fontSize: '0.76rem', fontWeight: 600, background: ef ? '#dcfce7' : '#fef3c7', color: ef ? '#166534' : '#92400e' }}>
                                                                                {ef ? f.eficacia : 'Por avaliar'}
                                                                            </span>
                                                                            {f.criterios_eficacia && (
                                                                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 3, maxWidth: 280 }}>{f.criterios_eficacia}</div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>

                                        <div className="worker-form-section full-width" style={{ marginTop: 20 }}>
                                            <h3><OrcamentoIcon name="document" fallback="📝" /> Registo manual</h3>
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Data Última Formação SST</label>
                                            <input type="date" name="formacao_profissional_data" value={formData.formacao_profissional_data} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field">
                                            <label>Formação acolhimento</label>
                                            <input type="date" name="formacao_acolhimento_data" value={formData.formacao_acolhimento_data} onChange={handleInputChange} />
                                        </div>
                                        <div className="worker-form-field" style={{ flexDirection: 'row', alignItems: 'center', marginTop: '25px' }}>
                                            <input type="checkbox" name="formacao_stc" checked={formData.formacao_stc} onChange={handleInputChange} />
                                            <label style={{ marginBottom: 0 }}>Formação STC?</label>
                                        </div>

                                        <div className="worker-form-field full-width">
                                            <label>Formações específicas/Qualificações Específicas</label>
                                            <textarea
                                                name="formacoes_especificas"
                                                value={formData.formacoes_especificas}
                                                onChange={handleInputChange}
                                                rows="4"
                                                placeholder="Ex: Montagem e Utilização de Andaimes, Trabalhos em Altura..."
                                            ></textarea>
                                        </div>

                                        <div className="worker-form-field full-width">
                                            <label>Observações Gerais</label>
                                            <textarea
                                                name="observacoes"
                                                value={formData.observacoes}
                                                onChange={handleInputChange}
                                                rows="4"
                                            ></textarea>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'documentos' && (
                                    <div className="worker-documents">
                                        <div className="worker-contract-panel" data-liquid-notice="true" data-liquid-tone={formData.contrato_pdf_path ? "success" : "warning"} style={{
                                            width: '100%',
                                            background: formData.contrato_pdf_path ? '#f0fff4' : '#fffaf0',
                                            border: `1px solid ${formData.contrato_pdf_path ? '#9ae6b4' : '#f6ad55'}`,
                                            borderRadius: '8px',
                                            padding: '15px 18px',
                                            marginBottom: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '16px',
                                            flexWrap: 'nowrap'
                                        }}>
                                            {/* minWidth 0 para o bloco poder encolher: sem isso o texto
                                                empurrava os botões para a linha de baixo. */}
                                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                                <h3 style={{ margin: 0, fontSize: '1rem', color: '#2d3748', whiteSpace: 'nowrap' }}><LiquidText>📑 Contrato de Trabalho (Individual)</LiquidText></h3>
                                                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#4a5568', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    <LiquidText>{formData.contrato_pdf_path
                                                        ? `✅ Contrato carregado${formData.contrato_carregado_em ? ' em ' + new Date(formData.contrato_carregado_em).toLocaleDateString('pt-PT') : ''}${formData.contrato_ja_assinado ? ' · já vinha assinado' : ''}.`
                                                        : '⚠️ Carregue aqui o PDF do contrato específico deste trabalhador antes de gerar o link de preenchimento.'}</LiquidText>
                                                </p>

                                            </div>

                                            {/* Data do contrato, centrada entre o título e os botões. É
                                                declarada aqui e é esta que vai para o PDF assinado e para
                                                futuros avisos de renovação. A leitura do PDF serve só para
                                                propor um valor — quem confirma é o utilizador, porque uma
                                                data errada num contrato é grave. */}
                                            {formData.contrato_pdf_path && (
                                                <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <label style={{ margin: 0, fontSize: '0.76rem', color: '#4a5568', fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        Data do contrato {dataObrigatoria && <span style={{ color: '#c53030' }}>*</span>}
                                                        {dataSugerida && formData.contrato_data === dataSugerida && (
                                                            <span style={{ color: '#b45309', fontWeight: 400 }}
                                                                title="Data lida do próprio PDF do contrato. Confirme antes de gravar.">
                                                                <OrcamentoIcon name="warning" fallback="⚠️" /> lida do PDF
                                                            </span>
                                                        )}
                                                        {!formData.contrato_data && (
                                                            <span style={{ color: '#c53030', fontWeight: 400 }}
                                                                title="Não foi possível ler a data no PDF — introduza-a.">
                                                                <OrcamentoIcon name="warning" fallback="⚠️" /> por preencher
                                                            </span>
                                                        )}
                                                    </label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <input type="date" name="contrato_data"
                                                            value={formData.contrato_data ? String(formData.contrato_data).substring(0, 10) : ''}
                                                            onChange={e => { setFormData({ ...formData, contrato_data: e.target.value }); setDataSugerida(null); }}
                                                            style={{
                                                                padding: '5px 8px', fontSize: '0.85rem', width: 150, marginBottom: 0,
                                                                borderColor: (dataObrigatoria && !formData.contrato_data) ? '#c53030' : undefined,
                                                            }} />
                                                        {/* Só os contratos a termo têm renovação a vigiar. O silêncio é
                                                            o estado por omissão, para os sem termo não gerarem ruído. */}
                                                        <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: '#4a5568', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                                            title="Contrato a termo: avisar da renovação no separador Notificações.">
                                                            <input type="checkbox"
                                                                checked={!!formData.contrato_alerta_ativo}
                                                                onChange={e => setFormData({ ...formData, contrato_alerta_ativo: e.target.checked })}
                                                                style={{ width: 'auto', marginBottom: 0 }} />
                                                            <OrcamentoIcon name="bell" fallback="🔔" /> alertar
                                                        </label>
                                                    </div>

                                                </div>
                                            )}
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flex: '0 0 auto' }}>
                                                {formData.contrato_pdf_path && (
                                                    <>
                                                        <button type="button" onClick={handleContratoVer} className="btn-secondary" style={{ padding: '8px 14px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}><OrcamentoIcon name="document" fallback="📄" /> Ver original</button>
                                                        {formData.assinatura_url && !formData.contrato_ja_assinado && (
                                                            <button type="button" onClick={async () => {
                                                                try {
                                                                    const token = localStorage.getItem('token');
                                                                    const res = await fetch(`${api.defaults.baseURL}/trabalhadores/${selectedTrabalhador.id}/contrato/signed-pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
                                                                    if (!res.ok) throw new Error();
                                                                    window.open(URL.createObjectURL(await res.blob()), '_blank');
                                                                } catch { liquidFeedback.notify('Erro ao gerar PDF assinado.'); }
                                                            }} className="btn-secondary" style={{ padding: '8px 14px', fontSize: '0.85rem', background: '#ebf8ff', color: '#2b6cb0', whiteSpace: 'nowrap' }}><LiquidText>✍️ PDF Assinado</LiquidText></button>
                                                        )}
                                                        <button type="button" onClick={handleContratoRemover} className="btn-secondary" style={{ padding: '8px 14px', fontSize: '0.85rem', background: '#fed7d7', color: '#c53030', whiteSpace: 'nowrap' }}><OrcamentoIcon name="trash" fallback="🗑️" /> Remover</button>
                                                    </>
                                                )}
                                                <label className="btn-primary" style={{ padding: '8px 14px', fontSize: '0.85rem', cursor: selectedTrabalhador ? 'pointer' : 'not-allowed', opacity: selectedTrabalhador ? 1 : 0.5, marginBottom: 0, whiteSpace: 'nowrap' }}>
                                                    <LiquidText>{formData.contrato_pdf_path ? '🔄 Substituir' : '📤 Carregar PDF'}</LiquidText>
                                                    <input type="file" accept="application/pdf" onChange={handleContratoUpload} disabled={!selectedTrabalhador} style={{ display: 'none' }} />
                                                </label>
                                            </div>
                                        </div>

                                        {(() => {
                                            const isAtivo = String(formData.ativo) === 'true';
                                            return (
                                                <div className="worker-declaration-panel" data-liquid-notice="true" data-liquid-tone="info" style={{
                                                    width: '100%',
                                                    background: isAtivo ? '#ebf8ff' : '#f7fafc',
                                                    border: `1px solid ${isAtivo ? '#90cdf4' : '#e2e8f0'}`,
                                                    borderRadius: '8px',
                                                    padding: '15px 18px',
                                                    marginBottom: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '12px',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <div style={{ flex: 1, minWidth: 240 }}>
                                                        <h3 style={{ margin: 0, fontSize: '1rem', color: '#2d3748' }}><LiquidText>📜 Declaração de Trabalho</LiquidText></h3>
                                                        <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#4a5568' }}>
                                                            <LiquidText>{isAtivo
                                                                ? 'Emite uma declaração em como o trabalhador se encontra ao serviço da empresa à data de hoje.'
                                                                : '⚠️ Disponível apenas para trabalhadores ativos.'}</LiquidText>
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleGerarDeclaracao}
                                                        disabled={!selectedTrabalhador || !isAtivo}
                                                        className="btn-primary"
                                                        style={{ padding: '8px 14px', fontSize: '0.85rem', opacity: (!selectedTrabalhador || !isAtivo) ? 0.5 : 1, cursor: (!selectedTrabalhador || !isAtivo) ? 'not-allowed' : 'pointer' }}
                                                    ><LiquidText>
                                                        📜 Gerar Declaração (PDF)
                                                    </LiquidText></button>
                                                </div>
                                            );
                                        })()}

                                        {evidencias.some(ev => ev.origem === 'propria' && ev.categoria === 'medicoes_sst') && (
                                            <section style={{ width: '100%', marginBottom: 20 }}>
                                                <h3><OrcamentoIcon name="document" fallback="📄" /> Avaliações e Medições SST</h3>
                                                <div className="worker-document-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                                                    {evidencias.filter(ev => ev.origem === 'propria' && ev.categoria === 'medicoes_sst').map(ev => (
                                                        <div key={ev.id} data-liquid-notice="true" data-liquid-tone="info"
                                                            style={{ padding: 14, border: '1px solid #cbd5e1', borderRadius: 8 }}>
                                                            <strong>{ev.titulo}</strong>
                                                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '8px 0' }}>
                                                                {ev.entidade}
                                                                {ev.data_documento && <> · Avaliação de {new Date(ev.data_documento).toLocaleDateString('pt-PT')}</>}
                                                            </p>
                                                            <button type="button" className="btn-secondary" onClick={() => abrirEvidencia(ev)}>
                                                                <OrcamentoIcon name="document" fallback="📄" /> Abrir ficha
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        )}

                                        <div style={{ width: '100%' }}>
                                            <h3><OrcamentoIcon name="document" fallback="📋" /> Aceitação de Documentos Internos</h3>
                                            <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '15px' }}>
                                                Registe aqui a entrega e aceitação dos documentos obrigatórios pelo trabalhador.
                                                O trabalhador deve ler o documento e assinar digitalmente.
                                            </p>

                                            <div className="worker-document-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginTop: '10px' }}>
                                                {AVAILABLE_DOCS.map(docName => {
                                                    const status = workerDocs.find(d => d.documento === docName);
                                                    const isAccepted = status?.aceite;
                                                    const docInternal = documentosInternos.find(d => d.nome === docName);
                                                    const hasPdf = !!docInternal;

                                                    return (
                                                        <div key={docName} className="worker-document-card" data-accepted={!!isAccepted} style={{
                                                            background: isAccepted ? '#f0fff4' : '#fff',
                                                            padding: '15px',
                                                            borderRadius: '8px',
                                                            border: `1px solid ${isAccepted ? '#9ae6b4' : '#edf2f7'}`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between'
                                                        }}>
                                                            <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '0.9rem', color: '#2d3748' }}>
                                                                {docName}
                                                            </div>
                                                            <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '12px' }}>
                                                                Estado: {isAccepted ?
                                                                    <span style={{ color: '#38a169', fontWeight: 'bold' }}><OrcamentoIcon name="check" fallback="✅" /> Lido e Aceite</span> :
                                                                    <span style={{ color: '#e53e3e' }}><OrcamentoIcon name="close" fallback="❌" /> Pendente</span>}
                                                            </div>
                                                            <div className="worker-document-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleOpenDoc(docName, isAccepted)}
                                                                    className={isAccepted ? "btn-secondary" : "btn-primary"}
                                                                    style={{
                                                                        flex: 1,
                                                                        padding: '8px',
                                                                        fontSize: '0.8rem',
                                                                        background: isAccepted ? '#fed7d7' : (hasPdf ? '#3182ce' : '#ed8936'),
                                                                        color: isAccepted ? '#c53030' : 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {isAccepted ? 'Revogar Aceitação' : (hasPdf ? 'Ler e Assinar' : 'Aceitar Manualmente')}
                                                                </button>
                                                                {isAccepted && hasPdf && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDownloadSignedPdf(docName, false)}
                                                                            style={{
                                                                                flex: 1,
                                                                                padding: '8px',
                                                                                fontSize: '0.75rem',
                                                                                background: '#2b6cb0',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            <OrcamentoIcon name="document" fallback="📄" /> PDF Assinado
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDownloadSignedPdf(docName, true)}
                                                                            style={{
                                                                                flex: 1,
                                                                                padding: '8px',
                                                                                fontSize: '0.75rem',
                                                                                background: '#38a169',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            <OrcamentoIcon name="calendar" fallback="📅" /> PDF c/ Data Hoje
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Documentos que já não estão em vigor. Chegam aqui sozinhos:
                                    quando entra um documento novo que substitui o anterior, o
                                    anterior sai das outras abas, das listas e da procura (a
                                    regra está em backend/utils/substituicao.js). Nada se apaga. */}
                                {activeTab === 'historico' && (
                                    <div>
                                        <div className="worker-form-section" style={{ marginBottom: 16 }}>
                                            <h3 style={{ marginBottom: 8 }}><OrcamentoIcon name="document" fallback="🗂️" /> Documentos substituídos</h3>
                                            <p style={{ margin: 0, fontSize: '0.86rem', color: '#4a5568' }}>
                                                Quando chega um documento novo, o anterior deixa de estar em vigor e passa para aqui.
                                                Fica guardado para histórico e para as auditorias, mas já não é o que conta.
                                            </p>
                                        </div>

                                        {nHistorico === 0 && (
                                            <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 14px', fontSize: '0.86rem', color: '#475569' }}>
                                                Sem documentos substituídos.
                                            </div>
                                        )}

                                        {/* Hoje só as fichas de aptidão se substituem. Se a regra
                                            passar a abranger outro tipo, os documentos aparecem em
                                            "Outros documentos" em vez de ficarem sem sítio. */}
                                        {[
                                            ['Fichas de aptidão', historico.evidencias.filter(e => e.categoria === 'fichas_aptidao')],
                                            ['Outros documentos', historico.evidencias.filter(e => e.categoria !== 'fichas_aptidao')],
                                        ].filter(([, lista]) => lista.length > 0).map(([titulo, lista]) => (
                                            <div key={titulo} style={{ marginBottom: 20 }}>
                                                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>
                                                    <LiquidText>{titulo}</LiquidText>
                                                </div>
                                                {lista.map(ev => {
                                                    const ficha = ev.categoria === 'fichas_aptidao';
                                                    return (
                                                        <div data-liquid-surface="true" key={ev.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <span style={{ background: '#94a3b8', color: '#fff', padding: '3px 10px', borderRadius: 5, fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.04em' }}>
                                                                    <OrcamentoIcon name="document" fallback="🗂️" /> {ficha ? 'SUBSTITUÍDA' : 'SUBSTITUÍDO'}
                                                                </span>
                                                                <span style={{ fontSize: '0.86rem', color: '#475569', fontWeight: 500 }}>
                                                                    {ficha ? <>Exame de {dataPt(ev.data_documento)}</> : <>{ev.titulo} · de {dataPt(ev.data_documento)}</>}
                                                                    {ev.validade && <> · {ficha ? 'válida' : 'válido'} até {dataPt(ev.validade)}</>}
                                                                    {ev.substituida_por && (
                                                                        <> · {ficha ? 'substituída pela' : 'substituído pelo'} de {dataPt(ev.substituida_por.data_documento)}</>
                                                                    )}
                                                                </span>
                                                                <div style={{ flex: 1 }} />
                                                                <button type="button" className="btn-secondary" style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                                    onClick={() => abrirEvidencia(ev)}>
                                                                    <OrcamentoIcon name="document" fallback="📄" /> {ficha ? 'Abrir ficha' : 'Abrir'}
                                                                </button>
                                                            </div>
                                                            {ev.entidade && <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 6, opacity: 0.85 }}>{ev.entidade}</div>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}

                                        {/* Aceitar a versão 1 de um documento não vale pela versão 2.
                                            A aceitação antiga guarda-se como prova do que a pessoa
                                            leu na altura; ao lado diz-se se a versão em vigor já foi
                                            aceite, que é o que interessa hoje. */}
                                        {historico.aceitacoes.length > 0 && (
                                            <div style={{ marginBottom: 20 }}>
                                                <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>
                                                    Aceitações de versões anteriores
                                                </div>
                                                {historico.aceitacoes.map(a => (
                                                    <div data-liquid-surface="true" key={a.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: '0.86rem', color: '#475569', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <strong style={{ color: '#334155' }}>{a.documento}</strong>
                                                        <span>
                                                            · versão {a.versao} {a.aceite ? <>aceite a {dataPt(a.data_aceitacao)}</> : <>sem aceitação</>}
                                                            {' '}· substituída pela versão {a.versao_em_vigor} —
                                                        </span>
                                                        {a.aceite_em_vigor ? (
                                                            <span style={{ border: '1px solid #0ca30c', background: 'rgba(12, 163, 12, 0.10)', color: '#1e293b', padding: '2px 9px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}><LiquidText>
                                                                ✓ aceite a </LiquidText>{dataPt(a.data_aceitacao_em_vigor)}
                                                            </span>
                                                        ) : (
                                                            <span style={{ border: '1px solid #fab219', background: 'rgba(250, 178, 25, 0.16)', color: '#1e293b', padding: '2px 9px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                <OrcamentoIcon name="warning" fallback="⚠️" /> ainda por aceitar
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'cartoes' && (
                                    <div>
                                        <p style={{ color: '#4a5568', marginTop: 0 }}>
                                            Cartões que esta pessoa usa. Pode ser mais do que um — o físico e o online.
                                            É esta escolha que faz aparecer, em <strong>Despesas → Cartões s/faturas</strong>,
                                            o botão para lhe pedir as faturas em falta por WhatsApp.
                                        </p>
                                        <p style={{ color: '#4a5568', marginTop: 0, fontSize: '13.5px' }}>
                                            <strong>M</strong> = Manutenção: com a caixa marcada, ao registar uma despesa com esse cartão
                                            escolhe-se Obra ou Manutenção
                                            {projetoManutencao
                                                ? <> (projeto <strong>{projetoManutencao}</strong>).</>
                                                : <> — falta o projeto da manutenção nos Dados da Empresa.</>}
                                        </p>

                                        {!formData.telefone?.trim() && (
                                            <div style={{
                                                background: '#fffaf0', border: '1px solid #f6ad55',
                                                borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
                                                color: '#7b341e', fontSize: '13.5px'
                                            }}>
                                                <OrcamentoIcon name="warning" fallback="⚠️" /> Sem <strong>Telemóvel</strong> preenchido nos Dados Pessoais não é
                                                possível enviar o pedido. Os cartões ficam associados na mesma.
                                            </div>
                                        )}

                                        <div className="cartoes-assoc-lista">
                                            {todosCartoes.length > 0 && (
                                                <div className="cartoes-assoc-cab">
                                                    <span>Cartão</span>
                                                    <span className="cartao-assoc-m" title="Manutenção">M</span>
                                                </div>
                                            )}
                                            {todosCartoes.map(card => {
                                                const meu = cartoesSel.includes(card.id);
                                                // "de outro" lê-se da lista carregada, que traz o dono de cada
                                                // cartão: escolher um já entregue tira-o a essa pessoa, e isso
                                                // tem de estar à vista antes de se carregar.
                                                const deOutro = !meu && card.trabalhador_id
                                                    && card.trabalhador_id !== selectedTrabalhador?.id;
                                                return (
                                                    <div key={card.id} className={`cartao-assoc ${meu ? 'escolhido' : ''}`}>
                                                        <label className="cartao-assoc-principal">
                                                            <input
                                                                type="checkbox"
                                                                checked={meu}
                                                                onChange={() => alternarCartao(card.id)}
                                                            />
                                                            <span className="cartao-assoc-nome">
                                                                {card.nome}
                                                                <small>{card.numero_mascarado} · {card.empresa || 'Sprod'}</small>
                                                            </span>
                                                            {deOutro && (
                                                                <span className="cartao-assoc-dono">
                                                                    de {card.trabalhador_nome}
                                                                </span>
                                                            )}
                                                        </label>
                                                        {/* M: com a caixa marcada, ao registar uma despesa com este
                                                            cartão escolhe-se Obra ou Manutenção. Marcar num cartão
                                                            que ainda não é desta pessoa associa-o também. */}
                                                        <span className="cartao-assoc-m">
                                                            <input
                                                                type="checkbox"
                                                                checked={meu && manutencaoSel.includes(card.id)}
                                                                onChange={() => {
                                                                    if (!meu) alternarCartao(card.id);
                                                                    alternarManutencao(card.id);
                                                                }}
                                                                title="Manutenção"
                                                                aria-label={`Manutenção — ${card.nome}`}
                                                            />
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            {todosCartoes.length === 0 && (
                                                <p style={{ color: '#718096' }}>Nenhum cartão ativo registado.</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="worker-modal-footer">
                                    {selectedTrabalhador && <button type="button" className="btn-secondary worker-copy-data" onClick={handleCopyWhatsApp} title="Copiar dados de identificação"><LiquidText>{whatsappCopied ? '✅ Copiado!' : '📋 Copiar Dados'}</LiquidText></button>}
                                    <button type="button" className="worker-btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button type="submit" className="btn-primary">Gravar Ficha</button>
                                </div>
                            </form>

                            {/* Modal Assinatura Manual */}
                            {showSignatureModal && (
                                <LiquidDialog onClose={() => setShowSignatureModal(false)} className="worker-modal-overlay" style={{ zIndex: 2000 }}>
                                    <div className="worker-modal" style={{ width: '90%', maxWidth: '600px', height: 'auto', maxHeight: '90vh' }}>
                                        <div className="worker-modal-header">
                                            <h2><LiquidText>{alvoAssinatura === 'rubrica_url' ? '✍️ Rubrica' : '✍️ Assinatura Manual'}</LiquidText></h2>
                                            <button className="close-btn" aria-label="Fechar" onClick={() => setShowSignatureModal(false)}><OrcamentoIcon name="close" fallback="×" /></button>
                                        </div>
                                        <div className="worker-modal-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
                                            <div data-liquid-surface="true" style={{ border: '2px dashed #cbd5e0', borderRadius: '8px', background: '#fff', width: '100%', maxWidth: '500px' }}>
                                                <canvas
                                                    ref={sigCanvasRef}
                                                    width={500}
                                                    height={300}
                                                    style={{
                                                        width: '100%', height: '300px',
                                                        touchAction: 'none', cursor: 'crosshair',
                                                        display: 'block'
                                                    }}
                                                    onMouseDown={startDraw}
                                                    onMouseMove={draw}
                                                    onMouseUp={stopDraw}
                                                    onMouseLeave={stopDraw}
                                                    onTouchStart={startDraw}
                                                    onTouchMove={draw}
                                                    onTouchEnd={stopDraw}
                                                />
                                            </div>
                                            <p style={{ color: '#718096', fontSize: '0.9rem', marginTop: '10px' }}>
                                                Assine na área acima com o dedo ou caneta stylus.
                                            </p>
                                            <div data-liquid-actions="true" style={{ display: 'flex', gap: '10px', marginTop: '20px', width: '100%', justifyContent: 'center' }}>
                                                <button type="button" className="btn-secondary" onClick={clearSignature}>Limpar</button>
                                                <button type="button" className="btn-primary" onClick={handleSaveSignature}>
                                                    {alvoAssinatura === 'rubrica_url' ? 'Confirmar Rubrica' : 'Confirmar Assinatura'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </LiquidDialog>
                            )}

                            {/* Modal de Leitura de PDF */}
                            {showPdfModal && (
                                <LiquidDialog onClose={() => setShowPdfModal(false)} className="worker-modal-overlay" style={{ zIndex: 3000 }}>
                                    <div className="worker-modal" style={{ width: '90%', maxWidth: '900px', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                                        <div className="worker-modal-header">
                                            <h2><OrcamentoIcon name="document" fallback="📄" /> Leitura de Documento: {currentDocName}</h2>
                                            <button className="close-btn" aria-label="Fechar" onClick={() => setShowPdfModal(false)}><OrcamentoIcon name="close" fallback="×" /></button>
                                        </div>
                                        <div className="worker-modal-content" style={{ flex: 1, padding: '0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                            <iframe
                                                src={pdfUrl}
                                                style={{ flex: 1, width: '100%', border: 'none' }}
                                                title="PDF Viewer"
                                            ></iframe>
                                        </div>
                                        <div className="worker-modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '15px' }}>
                                            <div style={{ fontSize: '0.9rem', color: '#4a5568' }}><LiquidText>
                                                ℹ️ Leia o documento até ao fim antes de aceitar.
                                            </LiquidText></div>
                                            <div data-liquid-actions="true" style={{ display: 'flex', gap: '10px' }}>
                                                <button type="button" className="worker-btn-cancel" onClick={() => setShowPdfModal(false)}>Cancelar</button>
                                                <button
                                                    type="button"
                                                    className="btn-primary"
                                                    onClick={() => handleAcceptDoc()}
                                                    style={{ background: '#38a169' }}
                                                >
                                                    <OrcamentoIcon name="check" fallback="✅" /> Li, Aceito e Assino
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </LiquidDialog>
                            )}
                            {/* Modal de Câmara Modal */}
                            {showCameraModal && (
                                <LiquidDialog onClose={stopCamera} className="worker-modal-overlay" style={{ zIndex: 3000 }}>
                                    <div className="worker-modal" style={{ width: '90%', maxWidth: '600px', textAlign: 'center' }}>
                                        <div className="worker-modal-header">
                                            <h2><OrcamentoIcon name="camera" fallback="📸" /> Tirar Foto</h2>
                                            <button className="close-btn" aria-label="Fechar" onClick={stopCamera}><OrcamentoIcon name="close" fallback="×" /></button>
                                        </div>
                                        <div className="worker-modal-content" style={{ padding: '20px' }}>
                                            {cameraError ? (
                                                <div style={{ color: '#e53e3e', padding: '20px', background: '#fed7d7', borderRadius: '8px' }}>
                                                    {cameraError}
                                                </div>
                                            ) : (
                                                <div style={{ position: 'relative', width: '100%', maxWidth: '100%', borderRadius: '8px', overflow: 'hidden', background: '#000', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                                                    <video
                                                        ref={videoRef}
                                                        autoPlay
                                                        playsInline
                                                        style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="worker-modal-footer" style={{ justifyContent: 'center', gap: '15px' }}>
                                            <button type="button" className="worker-btn-cancel" onClick={stopCamera}>
                                                Cancelar
                                            </button>
                                            {!cameraError && (
                                                <button type="button" className="btn-primary" onClick={capturePhoto} style={{ padding: '10px 20px', fontSize: '1rem' }}>
                                                    <OrcamentoIcon name="camera" fallback="📸" /> Capturar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </LiquidDialog>
                            )}
                        </div>
                    </LiquidDialog>

                )}
            </div>
        </div>
    );
};

export default Trabalhadores;
